import { useState, useRef, useEffect } from 'react';
import config from '../config';
import { authFetch, handleApiResponse, describeApiError } from '../api/authFetch';

export const useChatActions = ({ chatHistory, setChatHistory, navigate, showMessage, uploadStagedFiles, clearStagedFiles, stagedFiles, scrollToBottom, conversationId }) => {
  const [input, setInput] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingContent, setEditingContent] = useState("");
  const activeStreamController = useRef(null);
  const stoppedByUserRef = useRef(false);

  // Object URLs created below (sendMessage's attachedFiles thumbnails) live
  // as long as the just-sent message's local bubble does - unlike the
  // staged-file preview URLs in useFileUpload.js, they can't be revoked as
  // soon as they're created (that's the whole point, they back the <img>
  // src the student sees in the sent message) or at clearStagedFiles time
  // (called synchronously right after these are built - revoking there
  // would kill the thumbnail before it's ever painted). The natural end of
  // their lifecycle is this conversation view itself: switching threads
  // replaces chatHistory wholesale from the server (see useChatHistory's
  // loadHistory), which no longer references these blobs at all.
  const attachedFileObjectUrlsRef = useRef([]);
  useEffect(() => {
    return () => {
      attachedFileObjectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      attachedFileObjectUrlsRef.current = [];
    };
  }, [conversationId]);

  // Stop an in-flight chat response, mirroring the "stop generating" button
  // in Claude/ChatGPT. Keeps whatever text has streamed in so far.
  const stopGenerating = () => {
    if (activeStreamController.current) {
      stoppedByUserRef.current = true;
      activeStreamController.current.abort();
    }
  };

  // Send a new message.
  //
  // Accepts an optional textOverride so callers that already have the exact
  // text to send (starter-prompt cards, voice auto-send) can dispatch it
  // synchronously instead of staging it into `input` via setInput() and
  // waiting a tick for a fresh closure - `input` here is only a fallback for
  // the normal typed-and-click-send path. Returns a boolean: false means the
  // call bailed out before dispatching anything (no text, or a send already
  // in flight) so the caller can react (e.g. fall back to putting the text
  // in the composer); true means the message was accepted and is being
  // processed, even if it later fails asynchronously (that failure already
  // has its own recovery, e.g. the upload-failure branch below).
  //
  // `capabilities` is the composer's explicit "+" menu state
  // ({ createImage, useClassMaterials }, both booleans) - see ChatInput.js.
  // When a flag is on, it forces the corresponding branch below instead of
  // going through detectImageGenerationRequest/detectProjectContextRequest.
  // Callers that don't pass it (starter prompts, voice auto-send) fall
  // through to that keyword detection exactly as before, since
  // `capabilities` is undefined and `undefined?.x` is undefined, which is
  // falsy in the `||` checks below.
  const sendMessage = async (e, textOverride, capabilities) => {
    e.preventDefault();
    const currentInput = textOverride ?? input;
    if (!currentInput || isSearching) return false;

    // Prevent double submission by immediately setting searching state.
    // This happens synchronously (before any await below), so a second
    // call - even one fired before the first call's promise resolves -
    // still sees isSearching === true and bails above.
    setIsSearching(true);

    showMessage("", "error"); // Clear any existing messages

    // Always clear the composer on send. For the normal typed path this is
    // the existing "clear after send" behavior; for a textOverride send the
    // composer was never set to the sent text in the first place (callers
    // no longer stage it via setInput before calling), so this is a no-op
    // beyond clearing whatever unrelated text (if any) was left in the box.
    setInput("");

    // Check if this is an image generation request - forced on by the
    // composer's "Create an image" toggle regardless of wording, otherwise
    // falls back to keyword sniffing (see sendMessage's capabilities note).
    const isImageRequest = capabilities?.createImage || detectImageGenerationRequest(currentInput);

    // Upload staged files first if any
    let uploadedFileIds = [];
    if (stagedFiles && stagedFiles.length > 0) {
      try {
        const uploadedFiles = await uploadStagedFiles();
        uploadedFileIds = uploadedFiles.map(file => file.id);
      } catch (error) {
        console.error("Failed to upload staged files:", error);
        // Mirror the late-failure recovery in handleRegularChat's catch:
        // unlock the composer and give the student their typed text (or
        // override) back instead of leaving it disabled with no way to
        // retry. The guard above already passed, so this is a mid-flight
        // failure, not a bail - return true.
        setIsSearching(false);
        setInput(currentInput);
        showMessage("Failed to upload files", "error");
        return true;
      }
    }

    // Create display message for user (shows original input + staged files info)
    let displayMessage = currentInput;
    let attachedFiles = [];

    if (stagedFiles && stagedFiles.length > 0) {
      const fileNames = stagedFiles.map(f => f.name).join(', ');
      displayMessage = `${currentInput}\n\n📎 Attached files: ${fileNames}`;

      // Include file data for preview. Each created object URL is tracked
      // in attachedFileObjectUrlsRef so it gets revoked once this
      // conversation view is left - see that ref's declaration above.
      attachedFiles = stagedFiles.map(file => {
        const url = file.file ? URL.createObjectURL(file.file) : null;
        if (url) attachedFileObjectUrlsRef.current.push(url);
        return {
          id: file.id,
          name: file.name,
          size: file.size,
          type: file.type,
          url // Create preview URL
        };
      });
    }

    // Add user message to chat history (will be updated with ID later).
    // _clientId is a local-only tag (never sent to the backend) used to find
    // and roll this bubble back if the request that's supposed to persist it
    // fails - otherwise the message would keep showing as "sent" even though
    // it was never saved, and would vanish on the next reload with no
    // explanation.
    const clientMsgId = `pending_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const userMessage = {
      role: "user",
      content: displayMessage,
      attachedFiles: attachedFiles,
      timestamp: new Date().toISOString(),
      _clientId: clientMsgId
    };
    setChatHistory(prev => [...prev, userMessage]);

    // Scroll to bottom when user sends a message
    if (scrollToBottom) {
      setTimeout(() => scrollToBottom(), 50); // Small delay to ensure DOM update
    }

    // Clear staged files after adding to chat
    if (clearStagedFiles) {
      clearStagedFiles();
    }

    if (isImageRequest) {
      // Handle image generation
      await handleImageGeneration(currentInput);
    } else {
      // Handle regular chat
      await handleRegularChat(currentInput, uploadedFileIds, clientMsgId, capabilities);
    }
    return true;
  };

  // Function to detect if the message is an image generation request
  const detectImageGenerationRequest = (message) => {
    const imageKeywords = [
      'generate image', 'generate an image', 'generate a image',
      'create image', 'create an image', 'create a image',
      'make image', 'make an image', 'make a image',
      'draw image', 'draw an image', 'draw a image',
      'generate picture', 'generate a picture', 'generate an picture',
      'create picture', 'create a picture', 'create an picture',
      'make picture', 'make a picture', 'make an picture',
      'draw picture', 'draw a picture', 'draw an picture',
      'generate artwork', 'create artwork', 'make artwork',
      'image of', 'picture of', 'drawing of', 'artwork of',
      'paint', 'sketch', 'illustrate', 'visualize'
    ];

    const lowerMessage = message.toLowerCase();
    return imageKeywords.some(keyword => lowerMessage.includes(keyword));
  };

  // Detect if the message is about project/epic/requirement context
  const detectProjectContextRequest = (message) => {
    const keywords = [
      'epic', 'requirement', 'requirements', 'story', 'user story', 'feature', 'features', 'project', 'acceptance criteria', 'spec', 'specification', 'scope', 'backlog', 'sprint', 'milestone', 'roadmap', 'task', 'ticket', 'jira', 'issue', 'implementation', 'estimate', 'summary', 'design', 'architecture', 'use case', 'business rule', 'functional', 'non-functional', 'objective', 'goal', 'deliverable', 'release', 'deployment', 'test case', 'test plan', 'test', 'qa', 'quality assurance', 'definition of done', 'dod', 'epics', 'stories', 'requirements doc', 'requirements document', 'project doc', 'project document', 'project plan', 'project summary', 'project overview', 'project details', 'project description', 'project objective', 'project goal', 'project deliverable', 'project milestone', 'project roadmap', 'project backlog', 'project ticket', 'project issue', 'project implementation', 'project estimate', 'project design', 'project architecture', 'project use case', 'project business rule', 'project functional', 'project non-functional', 'project acceptance criteria', 'project test case', 'project test plan', 'project test', 'project qa', 'project quality assurance', 'project definition of done', 'project dod'
    ];
    const lowerMessage = message.toLowerCase();
    return keywords.some(keyword => lowerMessage.includes(keyword));
  };

  // Save a message to the database. Only role="user" is ever legitimate to
  // call this with - /save-message rejects any other client-supplied role
  // (see backend), because content attributed to the assistant must be
  // written by the server itself, never supplied by the client. Assistant
  // image-generation replies (success or failure) are written server-side
  // once a job reaches a terminal state - see
  // ImageGenerationService._persist_assistant_message - not through this
  // function.
  const saveMessageToDatabase = async (role, content) => {
    try {
      const response = await authFetch(`${config.API_BASE_URL}/save-message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          role: role,
          content: content,
          conversation_id: conversationId
        })
      });

      const data = await handleApiResponse(response, navigate);
      return data ? data.message_id : null;
    } catch (error) {
      console.error('Error saving message to database:', error);
      showMessage(`Failed to save message: ${describeApiError(error)}`, "error");
    }
    return null;
  };

  // Handle image generation
  const handleImageGeneration = async (prompt) => {
    // Save the user's image request to database
    const userMessageId = await saveMessageToDatabase("user", prompt);

    // Add loading indicator for image generation
    const loadingMessage = {
      role: "assistant",
      content: "🎨 Creating your image...",
      isLoading: true,
      isImageGeneration: true
    };
    setChatHistory(prev => [...prev, loadingMessage]);
    setIsSearching(true);

    try {
      // Clean the prompt - remove image generation keywords
      let cleanPrompt = prompt.toLowerCase();
      const prefixesToRemove = [
        'generate an image of', 'generate a image of', 'generate image of',
        'create an image of', 'create a image of', 'create image of',
        'make an image of', 'make a image of', 'make image of',
        'draw an image of', 'draw a image of', 'draw image of',
        'generate a picture of', 'generate an picture of', 'generate picture of',
        'create a picture of', 'create an picture of', 'create picture of',
        'make a picture of', 'make an picture of', 'make picture of',
        'draw a picture of', 'draw an picture of', 'draw picture of',
        'generate an image about', 'generate a image about', 'generate image about',
        'create an image about', 'create a image about', 'create image about',
        'make an image about', 'make a image about', 'make image about',
        'draw an image about', 'draw a image about', 'draw image about',
        'generate an image', 'generate a image', 'generate image',
        'create an image', 'create a image', 'create image',
        'make an image', 'make a image', 'make image',
        'draw an image', 'draw a image', 'draw image',
        'generate a picture', 'generate an picture', 'generate picture',
        'create a picture', 'create an picture', 'create picture',
        'make a picture', 'make an picture', 'make picture',
        'draw a picture', 'draw an picture', 'draw picture',
        'image of', 'picture of', 'drawing of', 'artwork of'
      ];

      for (const prefix of prefixesToRemove) {
        if (cleanPrompt.startsWith(prefix)) {
          cleanPrompt = cleanPrompt.substring(prefix.length).trim();
          break;
        }
      }

      // Use original prompt if nothing was removed to preserve proper capitalization
      if (cleanPrompt === prompt.toLowerCase()) {
        cleanPrompt = prompt;
      } else {
        // Capitalize first letter
        cleanPrompt = cleanPrompt.charAt(0).toUpperCase() + cleanPrompt.slice(1);
      }

      // Start image generation job
      const response = await authFetch(`${config.API_BASE_URL}/api/image-generation/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          // dall-e-3 is no longer available on this OpenAI account ("model
          // does not exist"); gpt-image-1 is the replacement and has no
          // style param and different quality values.
          prompt: cleanPrompt,
          model: 'gpt-image-1',
          size: '1024x1024',
          quality: 'medium',
          // Lets the server attach the eventual assistant reply to this
          // thread and write it itself once the job finishes - see
          // ImageGenerationService._persist_assistant_message. Without
          // this, the job has no thread to persist a reply into.
          conversation_id: conversationId
        })
      });

      const data = await handleApiResponse(response, navigate);
      if (data === null) return; // redirected to signin

      if (data.success) {
        // Poll for job completion
        await pollImageGeneration(data.job_id, cleanPrompt, prompt, userMessageId);
      } else {
        setIsSearching(false);
        const errorMessage = `❌ Failed to generate image: ${data.error || 'Unknown error'}`;

        // No job was ever created here (create_job itself rejected the
        // request, e.g. rate limit / invalid params), so there is no
        // server-side job to persist this failure against, and
        // /save-message correctly rejects any client-supplied
        // role="assistant" content. Show it locally only - it won't
        // survive a reload, but it also won't fabricate assistant history.
        setChatHistory(prev => {
          const withoutLoading = prev.filter(msg => !msg.isLoading);
          return [...withoutLoading, {
            role: "assistant",
            content: errorMessage,
            isError: true,
            liveError: true
          }];
        });
      }
    } catch (error) {
      setIsSearching(false);
      const errorMessage = `❌ Failed to generate image: ${describeApiError(error)}`;

      // Same as above: the /generate request itself failed (network error),
      // so no job exists server-side to attach a persisted reply to.
      setChatHistory(prev => {
        const withoutLoading = prev.filter(msg => !msg.isLoading);
        return [...withoutLoading, {
          role: "assistant",
          content: errorMessage,
          isError: true,
          liveError: true
        }];
      });
    }
  };

  // Poll for image generation completion
  const pollImageGeneration = async (jobId, cleanPrompt, originalPrompt, userMessageId) => {
    const maxAttempts = 60; // 5 minutes at 5-second intervals
    let attempts = 0;

    const checkStatus = async () => {
      try {
        const response = await authFetch(`${config.API_BASE_URL}/api/image-generation/job/${jobId}/status`);

        if (response.status === 401) {
          setIsSearching(false);
          navigate('/signin');
          return;
        }
        if (response.status === 403) {
          setIsSearching(false);
          const data = await response.json().catch(() => ({}));
          const errorMessage = `❌ ${data.detail || 'You do not have permission to check this image generation job.'}`;
          // Not persisted: this is a rejected ownership check on someone
          // else's job, not this user's own assistant reply to write.
          setChatHistory(prev => {
            const withoutLoading = prev.filter(msg => !msg.isLoading);
            return [...withoutLoading, { role: "assistant", content: errorMessage, isError: true, liveError: true }];
          });
          return;
        }

        const jobData = await response.json();

        if (jobData.status === 'completed') {
          setIsSearching(false);

          // Construct full image URL if it's relative
          let imageUrl = jobData.image_url;
          if (imageUrl && !imageUrl.startsWith('http')) {
            imageUrl = `${config.API_BASE_URL}${imageUrl}`;
          }

          const assistantMessage = `🎨 I've created an image based on your request: "${cleanPrompt}"`;

          // The server already persisted this exact assistant reply itself
          // when the job completed (ImageGenerationService.
          // _persist_assistant_message) - it has the real job context
          // (owning user + thread) to do so trustworthily, which this
          // client-side call never did. This only updates the live view;
          // no /save-message call needed (and role="assistant" would be
          // rejected there regardless).
          setChatHistory(prev => {
            const withoutLoading = prev.filter(msg => !msg.isLoading);
            return [...withoutLoading, {
              role: "assistant",
              content: assistantMessage,
              timestamp: new Date().toISOString(),
              imageUrl: imageUrl,
              isImageResult: true,
              imagePrompt: cleanPrompt,
              jobId: jobId
            }];
          });

        } else if (jobData.status === 'failed') {
          setIsSearching(false);
          const errorMessage = `❌ Image generation failed: ${jobData.error_message || 'Unknown error'}`;

          // Already persisted server-side for the same reason as the
          // completed branch above - the job reaching "failed" means
          // process_image_generation_job already wrote this failure as an
          // assistant message before this poll ever saw the status.
          setChatHistory(prev => {
            const withoutLoading = prev.filter(msg => !msg.isLoading);
            return [...withoutLoading, {
              role: "assistant",
              content: errorMessage,
              isError: true,
              liveError: true
            }];
          });

        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(checkStatus, 5000); // Check again in 5 seconds
        } else {
          setIsSearching(false);
          const timeoutMessage = "❌ Image generation timed out. Please try again.";

          // Not persisted from here: the backend's own stale-job cleanup
          // (ImageJobQueue.job_timeout, 5 minutes - the same budget this
          // client-side polling loop uses) independently marks the job
          // failed and persists an assistant failure message once it hits
          // that timeout server-side, so this is just the client giving up
          // on polling, not a second source of truth to write from.
          setChatHistory(prev => {
            const withoutLoading = prev.filter(msg => !msg.isLoading);
            return [...withoutLoading, {
              role: "assistant",
              content: timeoutMessage,
              isError: true,
              liveError: true
            }];
          });
        }
      } catch (error) {
        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(checkStatus, 5000);
        } else {
          setIsSearching(false);
          const errorMessage = `❌ Error checking image generation status: ${describeApiError(error)}`;

          // Not persisted: this is a network/client-side failure checking
          // status, not a server-confirmed terminal job state.
          setChatHistory(prev => {
            const withoutLoading = prev.filter(msg => !msg.isLoading);
            return [...withoutLoading, {
              role: "assistant",
              content: errorMessage,
              isError: true,
              liveError: true
            }];
          });
        }
      }
    };

    checkStatus();
  };

  // Shared SSE reader for /chat/stream, used by every flow that needs a
  // live-streamed reply (new messages, edited/regenerated messages) so none
  // of them freeze with a static "please wait" bubble while the backend
  // works - each reports the same live "step"/token progress as it arrives.
  // Returns the parsed `done` event payload, or { redirected: true } if the
  // caller should stop (401 redirect, or a non-OK response already reported
  // via handleApiResponse).
  const streamChatRequest = async (requestBody, { signal, onStep, onToken } = {}) => {
    const res = await authFetch(`${config.API_BASE_URL}/chat/stream`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestBody),
    }, { timeoutMs: 120000, signal }); // generation can take 20-30s+ for document-enhanced replies

    if (res.status === 401) {
      navigate("/signin");
      return { redirected: true };
    }
    if (!res.ok || !res.body) {
      await handleApiResponse(res, navigate); // throws ApiError with the backend's detail
      return { redirected: true };
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let finalData = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let boundary;
      while ((boundary = buffer.indexOf("\n\n")) !== -1) {
        const rawEvent = buffer.slice(0, boundary).trim();
        buffer = buffer.slice(boundary + 2);
        if (!rawEvent.startsWith("data:")) continue;

        let evt;
        try {
          evt = JSON.parse(rawEvent.slice(5).trim());
        } catch (e) {
          continue; // ignore a malformed frame rather than aborting the whole stream
        }

        if (evt.error) {
          throw new Error(evt.error);
        }
        if (evt.step && onStep) {
          // evt.search_meta (present only on the web-search step) carries the
          // real query + result list, so the UI can render an actual search
          // trace card instead of just narrating a generic status string.
          onStep(evt.step, evt.search_meta || null);
        }
        if (evt.token && onToken) {
          onToken(evt.token);
        }
        if (evt.done) {
          finalData = evt;
        }
      }
    }

    if (!finalData) {
      throw new Error("Connection closed before the response finished.");
    }
    return finalData;
  };

  // Builds the onStep/onToken callbacks shared by every consumer of
  // streamChatRequest, so a live step or token always updates the last
  // ("loading"/"streaming") assistant bubble in the chat history the same
  // way regardless of which flow (new message vs. edit-and-regenerate)
  // triggered it.
  const makeStreamCallbacks = (steps, streamState) => ({
    onStep: (step, searchMeta) => {
      // Plain string steps render as today; a step carrying searchMeta is
      // stored as a structured entry so ThinkingIndicator can render a real,
      // persistent web-search card instead of a single overwritable label.
      // The backend emits the search-meta step TWICE (once as soon as the
      // query is known, with searchMeta.pending=true and no results yet,
      // then again once the search completes with the real results) - the
      // second frame should UPDATE the same card in place, not add a
      // second one, so find-and-replace any existing search-meta entry
      // instead of always appending.
      if (searchMeta) {
        const entry = { text: step, searchMeta };
        const existingIdx = steps.findIndex((s) => s && typeof s === "object" && s.searchMeta);
        if (existingIdx !== -1) {
          steps[existingIdx] = entry;
        } else {
          steps.push(entry);
        }
      } else {
        steps.push(step);
      }
      const currentSteps = [...steps];
      setChatHistory(prev => {
        const updated = [...prev];
        const lastIdx = updated.length - 1;
        if (updated[lastIdx]?.role === "assistant" && updated[lastIdx].isLoading) {
          updated[lastIdx] = { ...updated[lastIdx], content: step, steps: currentSteps };
        }
        return updated;
      });
    },
    onToken: (token) => {
      const isFirstToken = !streamState.startedStreaming;
      streamState.accumulated += token;
      streamState.startedStreaming = true;
      const currentContent = streamState.accumulated;
      setChatHistory(prev => {
        const updated = [...prev];
        const lastIdx = updated.length - 1;
        if (updated[lastIdx]?.role === "assistant") {
          if (isFirstToken && updated[lastIdx].thinkingStartedAt) {
            streamState.thinkingDurationMs = Date.now() - updated[lastIdx].thinkingStartedAt;
          }
          updated[lastIdx] = {
            ...updated[lastIdx],
            content: currentContent,
            isLoading: false,
            isStreaming: true,
            thinkingDurationMs: streamState.thinkingDurationMs
          };
        }
        return updated;
      });
    }
  });

  // Handle regular chat (non-image generation)
  // Streams the reply from /chat/stream (SSE) so the assistant bubble fills in
  // token-by-token instead of staying blank until the whole reply is ready.
  const handleRegularChat = async (currentInput, uploadedFileIds, clientMsgId, capabilities) => {
    // Add loading indicator for bot response
    const loadingMessage = {
      role: "assistant",
      content: "Thinking",
      isLoading: true,
      thinkingStartedAt: Date.now()
    };
    setChatHistory(prev => [...prev, loadingMessage]);

    const controller = new AbortController();
    activeStreamController.current = controller;
    stoppedByUserRef.current = false;
    const steps = [];
    const streamState = { accumulated: "", startedStreaming: false, thinkingDurationMs: null };

    try {
      const requestBody = {
        message: currentInput,
        conversation_id: conversationId
      };

      // Include file references if any were uploaded
      if (uploadedFileIds.length > 0) {
        requestBody.file_ids = uploadedFileIds;
      }
      // Add project context flag - forced on by the composer's "Use class
      // materials" toggle, otherwise falls back to keyword sniffing (see
      // sendMessage's capabilities note above).
      if (capabilities?.useClassMaterials || detectProjectContextRequest(currentInput)) {
        requestBody.should_use_documents = true;
      }

      const data = await streamChatRequest(requestBody, {
        signal: controller.signal,
        ...makeStreamCallbacks(steps, streamState)
      });

      setIsSearching(false);

      if (data.redirected) {
        return;
      }
      const startedStreaming = streamState.startedStreaming;
      const thinkingDurationMs = streamState.thinkingDurationMs;

      setChatHistory(prev => {
        const withoutLoading = startedStreaming ? [...prev] : prev.filter(msg => !msg.isLoading);
        const assistantIdx = withoutLoading.length - 1;

        // Add IDs to the user message and new assistant message
        const updatedHistory = withoutLoading.map((msg, index) => {
          if (index === assistantIdx - 1 && msg.role === "user" && !msg.id) {
            return {
              ...msg,
              id: data.user_message_id,
              // Include document metadata if user uploaded a document
              document_id: data.user_document_id,
              document_url: data.user_document_url,
              document_filename: data.user_document_filename,
              document_type: data.user_document_type
            };
          }
          return msg;
        });

        // Finalize the assistant message
        const assistantMessage = {
          id: data.bot_message_id,
          role: "assistant",
          content: data.reply,
          timestamp: data.timestamp || new Date().toISOString(),
          used_search: data.used_search,
          used_documents: data.used_documents,
          used_uploaded_files: data.used_uploaded_files,
          searchResults: data.search_results || null,
          // Include document metadata if present
          document_id: data.document_id,
          document_url: data.document_url,
          document_filename: data.document_filename,
          document_type: data.document_type,
          is_document_result: data.is_document_result || false
        };

        // Add code execution result if present
        if (data.code_execution && data.code_result) {
          assistantMessage.codeExecutionResult = data.code_result.response;
          assistantMessage.isCodeExecution = true;
        }
        if (steps.length > 0) {
          assistantMessage.steps = steps;
        }
        if (thinkingDurationMs !== null) {
          assistantMessage.thinkingDurationMs = thinkingDurationMs;
        }

        if (startedStreaming) {
          updatedHistory[assistantIdx] = assistantMessage;
          return updatedHistory;
        }
        return [...updatedHistory, assistantMessage];
      });

      // Scroll to bottom when assistant responds
      if (scrollToBottom) {
        setTimeout(() => scrollToBottom(), 100);
      }
    } catch (error) {
      setIsSearching(false);

      if (error.name === "AbortError" && stoppedByUserRef.current) {
        // User hit "Stop" - keep whatever text has streamed in so far,
        // same as Claude/ChatGPT do, instead of treating it as an error.
        setChatHistory(prev => {
          const updated = [...prev];
          const lastIdx = updated.length - 1;
          const lastMsg = updated[lastIdx];
          if (lastMsg?.role === "assistant" && (lastMsg.isLoading || lastMsg.isStreaming)) {
            if (streamState.accumulated) {
              updated[lastIdx] = { ...lastMsg, content: streamState.accumulated, isLoading: false, isStreaming: false, stopped: true };
            } else {
              updated.pop();
            }
          }
          return updated;
        });
        return;
      }

      console.error("Failed to send message:", error);
      // The request that was supposed to save this message failed, so
      // remove the optimistic bubble (it was never actually sent - it would
      // just vanish on the next reload anyway) and give the student their
      // text back so they don't have to retype it before trying again.
      setChatHistory(prev => prev.filter(msg => !msg.isLoading && !msg.isStreaming && msg._clientId !== clientMsgId));
      if (clientMsgId) setInput(currentInput);
      showMessage(describeApiError(error), "error");
    } finally {
      activeStreamController.current = null;
    }
  };

  // Start editing a message
  const startEditMessage = (messageIndex, content) => {
    setEditingMessageId(messageIndex);
    setEditingContent(content);
  };

  // Cancel editing
  const cancelEditMessage = () => {
    setEditingMessageId(null);
    setEditingContent("");
  };

  // Save edited message and regenerate response.
  //
  // Persists the edit through PUT /messages/{id} (so the backend has the
  // authoritative, safety-scanned content - see app/routes/chat.py's
  // edit_message) BEFORE asking for a new reply, then streams the
  // regenerated reply from /chat/stream (SSE) via the same shared helper
  // handleRegularChat uses, so this flow shows the same live step/token
  // progress instead of a single static "Regenerating response..." bubble
  // that doesn't change until the entire reply is ready.
  //
  // The /chat/stream call passes regenerate_message_id so the backend
  // cleans up the extra "user" row its normal pipeline would otherwise
  // insert for this turn - without that, reloading the page would show
  // both the original and the edited message, plus two assistant replies
  // (see app/routes/chat.py's _cleanup_regenerate_duplicate for how).
  const saveEditedMessage = async (messageIndex) => {
    if (!editingContent.trim()) return;

    const editedContent = editingContent.trim();
    const originalMessage = chatHistory[messageIndex];

    if (!originalMessage?.id) {
      // Nothing on the backend to edit yet (e.g. an optimistic bubble that
      // hasn't finished sending) - bail out rather than silently
      // regenerating a reply for a message that was never persisted.
      showMessage("This message can't be edited yet - please wait for it to finish sending.", "error");
      return;
    }

    // Persist the edit first. If this fails, stop here - don't touch local
    // state or start a regeneration for content the backend never saved.
    try {
      const editRes = await authFetch(`${config.API_BASE_URL}/messages/${originalMessage.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: editedContent }),
      });
      const editData = await handleApiResponse(editRes, navigate);
      if (editData === null) return; // redirected to signin
    } catch (error) {
      console.error("Failed to save edited message:", error);
      showMessage(describeApiError(error), "error");
      return;
    }

    // Update the message in the local state first
    const updatedHistory = [...chatHistory];
    updatedHistory[messageIndex] = {
      ...originalMessage,
      content: editedContent,
      edited: true
    };

    // Remove all messages after the edited one (they'll be regenerated)
    const messagesToKeep = updatedHistory.slice(0, messageIndex + 1);

    // Add loading indicator for bot response
    const loadingMessage = {
      role: "assistant",
      content: "Thinking",
      isLoading: true,
      thinkingStartedAt: Date.now()
    };

    setChatHistory([...messagesToKeep, loadingMessage]);
    setIsSearching(true);

    setEditingMessageId(null);
    setEditingContent("");

    const controller = new AbortController();
    activeStreamController.current = controller;
    stoppedByUserRef.current = false;
    const steps = [];
    const streamState = { accumulated: "", startedStreaming: false, thinkingDurationMs: null };

    try {
      const data = await streamChatRequest({
        message: editedContent,
        conversation_id: conversationId,
        regenerate_message_id: originalMessage.id
      }, {
        signal: controller.signal,
        ...makeStreamCallbacks(steps, streamState)
      });

      setIsSearching(false);

      if (data.redirected) {
        return;
      }
      const startedStreaming = streamState.startedStreaming;
      const thinkingDurationMs = streamState.thinkingDurationMs;

      setChatHistory(prev => {
        const withoutLoading = startedStreaming ? [...prev] : prev.filter(msg => !msg.isLoading);

        // Create assistant message object
        const assistantMessage = {
          id: data.bot_message_id,
          role: "assistant",
          content: data.reply,
          timestamp: data.timestamp || new Date().toISOString(),
          used_search: data.used_search || false,
          used_documents: data.used_documents || false,
          used_uploaded_files: data.used_uploaded_files || false,
          searchResults: data.search_results || null
        };

        // Add code execution result if present
        if (data.code_execution && data.code_result) {
          assistantMessage.codeExecutionResult = data.code_result.response;
          assistantMessage.isCodeExecution = true;
        }
        if (steps.length > 0) {
          assistantMessage.steps = steps;
        }
        if (thinkingDurationMs !== null) {
          assistantMessage.thinkingDurationMs = thinkingDurationMs;
        }

        if (startedStreaming) {
          const lastIdx = withoutLoading.length - 1;
          withoutLoading[lastIdx] = assistantMessage;
          return withoutLoading;
        }
        return [...withoutLoading, assistantMessage];
      });

    } catch (error) {
      setIsSearching(false);

      if (error.name === "AbortError" && stoppedByUserRef.current) {
        // User hit "Stop" - keep whatever text has streamed in so far,
        // same as the new-message flow does, instead of treating it as an error.
        setChatHistory(prev => {
          const updated = [...prev];
          const lastIdx = updated.length - 1;
          const lastMsg = updated[lastIdx];
          if (lastMsg?.role === "assistant" && (lastMsg.isLoading || lastMsg.isStreaming)) {
            if (streamState.accumulated) {
              updated[lastIdx] = { ...lastMsg, content: streamState.accumulated, isLoading: false, isStreaming: false, stopped: true };
            } else {
              updated.pop();
            }
          }
          return updated;
        });
        return;
      }

      console.error("Failed to edit message:", error);
      showMessage(describeApiError(error), "error");
      setChatHistory(prev => prev.filter(msg => !msg.isLoading && !msg.isStreaming));
    } finally {
      activeStreamController.current = null;
    }
  };

  // Delete message and its response
  const deleteMessage = async (messageIndex) => {
    // Show confirmation dialog
    if (!window.confirm("Are you sure you want to delete this message and its response?")) {
      return;
    }

    try {
      const messageToDelete = chatHistory[messageIndex];

      // Check if message has an ID (needed for backend deletion)
      if (messageToDelete.id) {
        // Call backend to delete from database
        const res = await authFetch(`${config.API_BASE_URL}/messages/${messageToDelete.id}`, {
          method: "DELETE",
        });

        const data = await handleApiResponse(res, navigate);
        if (data === null) return; // redirected to signin
      }

      // Update local state
      setChatHistory(prev => {
        const newHistory = [...prev];

        // If it's a user message, delete it and the next assistant message (if exists)
        if (newHistory[messageIndex]?.role === "user") {
          // Remove the user message
          newHistory.splice(messageIndex, 1);

          // Check if the next message is an assistant response and remove it too
          if (newHistory[messageIndex]?.role === "assistant") {
            newHistory.splice(messageIndex, 1);
          }
        }

        return newHistory;
      });

      showMessage("Message deleted successfully", "success");

    } catch (error) {
      console.error("Failed to delete message:", error);
      showMessage(describeApiError(error), "error");
    }
  };

  // Logout function
  const handleLogout = async () => {
    try {
      await authFetch(`${config.API_BASE_URL}/api/logout`, {
        method: "POST",
      });
    } catch (error) {
      console.error("Logout request failed:", error);
    }
    navigate("/signin");
  };

  return {
    input,
    setInput,
    isSearching,
    editingMessageId,
    editingContent,
    setEditingContent,
    sendMessage,
    stopGenerating,
    startEditMessage,
    cancelEditMessage,
    saveEditedMessage,
    deleteMessage,
    handleLogout
  };
};
